//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  getAudienceBestTimesToPost,
  getAudienceDailyStats,
  getAudienceOnlineFollowersHeatmap,
  getAudienceSummaryStats,
} from '@apis/tiktok-analytics/tiktokAnalyticsApi';
import {
  ITikTokAudienceSummaryStats,
  ITikTokBestTimesToPost,
  ITikTokOnlineFollowersHeatmap,
} from '@apis/tiktok-analytics/tiktokAnalyticsApiModel';
import {
  IAliasedDateRange,
  getAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { fillInMissingAudienceDates } from '@tiktok/components/analytics/audience/tiktokAudienceUtils';
import {
  getIsFetchingBestTimesToPost,
  getIsFetchingOnlineFollowersHeatmap,
} from '@tiktok/redux/audience/selectors/getTikTokAudience';
import {
  ITikTokAudienceState,
  ITikTokDailyAudienceMetric,
} from '@tiktok/redux/audience/tiktokAudienceModel';
import { getTikTokUserId } from '@tiktok/redux/user/selectors/getTikTokUser';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

import { crl8 } from 'app/curalate.globals';

const initialState: ITikTokAudienceState = {
  bestTimesToPost: undefined,
  dateRange: getAliasedDateRange(
    'last7Days',
    crl8.session?.timeZone.id ?? TimeZoneId.Utc,
  ),
  dailyStats: [],
  earliestMetricsDate: undefined,
  onlineFollowersHeatmap: undefined,
  summaryStats: undefined,
  flags: {
    hasFetchedEarliestMetricsDate: false,
    isFetchingBestTimesToPost: false,
    isFetchingDailyStats: false,
    isFetchingOnlineFollowersHeatmap: false,
    isFetchingSummaryStats: false,
  },
  errors: {
    fetchBestTimesToPostError: undefined,
    fetchDailyStatsError: undefined,
    fetchOnlineFollowersHeatMapError: undefined,
    fetchSummaryStatsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.TikTokAudience,
  initialState,
  reducers: {
    updateDateRange(state, action: PayloadAction<IAliasedDateRange>) {
      state.dateRange = action.payload;
    },
    requestAudienceDailyStats(state) {
      state.flags.isFetchingDailyStats = true;
      state.errors.fetchDailyStatsError = undefined;
    },
    receiveAudienceDailyStatsSucceeded(
      state,
      action: PayloadAction<{
        dailyStats: ITikTokDailyAudienceMetric[];
        earliestMetricsDate: DateString | undefined;
      }>,
    ) {
      state.flags.isFetchingDailyStats = false;
      state.flags.hasFetchedEarliestMetricsDate = true;
      state.dailyStats = action.payload.dailyStats;
      state.earliestMetricsDate = action.payload.earliestMetricsDate;
    },
    receiveAudienceDailyStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingDailyStats = false;
      state.errors.fetchDailyStatsError = action.payload;
    },
    requestAudienceSummaryStats(state) {
      state.flags.isFetchingSummaryStats = true;
      state.errors.fetchSummaryStatsError = undefined;
    },
    receiveAudienceSummaryStatsSucceeded(
      state,
      action: PayloadAction<ITikTokAudienceSummaryStats>,
    ) {
      state.flags.isFetchingSummaryStats = false;
      state.summaryStats = action.payload;
    },
    receiveAudienceSummaryStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingSummaryStats = false;
      state.errors.fetchSummaryStatsError = action.payload;
    },
    requestOnlineFollowersHeatmap(state) {
      state.flags.isFetchingOnlineFollowersHeatmap = true;
      state.errors.fetchOnlineFollowersHeatMapError = undefined;
    },
    receiveOnlineFollowersHeatmapSucceeded(
      state,
      action: PayloadAction<ITikTokOnlineFollowersHeatmap>,
    ) {
      state.flags.isFetchingOnlineFollowersHeatmap = false;
      state.onlineFollowersHeatmap = action.payload.onlineFollowers;
    },
    receiveOnlineFollowersHeatmapFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingOnlineFollowersHeatmap = false;
      state.errors.fetchOnlineFollowersHeatMapError = action.payload;
    },
    requestBestTimesToPost(state) {
      state.flags.isFetchingBestTimesToPost = true;
      state.errors.fetchBestTimesToPostError = undefined;
    },
    receiveBestTimesToPostSucceeded(
      state,
      action: PayloadAction<ITikTokBestTimesToPost>,
    ) {
      state.flags.isFetchingBestTimesToPost = false;
      state.bestTimesToPost = action.payload.bestTimes;
    },
    receiveBestTimesToPostFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingBestTimesToPost = false;
      state.errors.fetchBestTimesToPostError = action.payload;
    },
  },
});

export const changeDateRangeAndRefreshContent = createThunk(
  (dateRange: IAliasedDateRange) => (dispatch) => {
    dispatch(actions.updateDateRange(dateRange));

    void dispatch(fetchAudienceDailyStats(dateRange));
    void dispatch(fetchAudienceSummaryStats(dateRange));
  },
);

export const fetchAudienceDailyStats = createThunk(
  (dateRange: IAliasedDateRange) => async (dispatch, getState) => {
    const state = getState();
    const tiktokUserId = getTikTokUserId(state);
    const userTimeZoneId = getUserTimeZone(state);

    // If the tiktokUserId is undefined, do not attempt to fetch user daily stats
    if (!isDefined(tiktokUserId)) {
      return;
    }

    dispatch(actions.requestAudienceDailyStats());

    try {
      const statsResponse = await getAudienceDailyStats({
        tiktokUserId: tiktokUserId,
        startTime: DateString.toDate(dateRange.startDate, userTimeZoneId),
        endTime: DateString.toDate(dateRange.endDate, userTimeZoneId),
      });

      const dailyStats = fillInMissingAudienceDates(
        statsResponse.dailyMetrics,
        dateRange.startDate,
        dateRange.endDate,
      );

      const dailyStatsResponse = {
        dailyStats,
        earliestMetricsDate: statsResponse.earliestDate
          ? DateString.fromTimestamp(statsResponse.earliestDate, userTimeZoneId)
          : undefined,
      };

      dispatch(actions.receiveAudienceDailyStatsSucceeded(dailyStatsResponse));
    } catch (e) {
      dispatch(
        actions.receiveAudienceDailyStatsFailed(
          getRequestErrorMessage(e) ??
            'An unknown error eccurred when attempting to fetch TikTok user audience daily stats',
        ),
      );
    }
  },
);

export const fetchAudienceSummaryStats = createThunk(
  (dateRange: IAliasedDateRange) => async (dispatch, getState) => {
    const state = getState();
    const tiktokUserId = getTikTokUserId(state);
    const userTimeZoneId = getUserTimeZone(state);

    // If the tiktokUserId is undefined, do not attempt to fetch user summary stats
    if (!isDefined(tiktokUserId)) {
      return;
    }

    dispatch(actions.requestAudienceSummaryStats());

    try {
      const summaryStats = await getAudienceSummaryStats({
        tiktokUserId: tiktokUserId,
        startTime: DateString.toDate(dateRange.startDate, userTimeZoneId),
        endTime: DateString.toDate(dateRange.endDate, userTimeZoneId),
      });

      dispatch(actions.receiveAudienceSummaryStatsSucceeded(summaryStats));
    } catch (e) {
      dispatch(
        actions.receiveAudienceSummaryStatsFailed(
          getRequestErrorMessage(e) ??
            'An unknown error eccurred when attempting to fetch TikTok user audience summary stats',
        ),
      );
    }
  },
);

/**
 * Fetches the client's most recent TikTok online followers data by hour, averaged
 * over the past 30 days. `onlineFollowersHeatmap.onlineFollowers` is undefined if there is less than 7 days of data.
 */
export const fetchOnlineFollowersHeatmap = createThunk(
  () => async (dispatch, getState) => {
    const state = getState();

    const isFetchingOnlineFollowersHeatmap =
      getIsFetchingOnlineFollowersHeatmap(state);
    const tiktokUserId = getTikTokUserId(state);
    const userTimeZoneId = getUserTimeZone(state);

    if (!isDefined(tiktokUserId) || isFetchingOnlineFollowersHeatmap) {
      return;
    }

    dispatch(actions.requestOnlineFollowersHeatmap());

    try {
      const onlineFollowersHeatmap = await getAudienceOnlineFollowersHeatmap({
        tiktokUserId,
        timeZone: userTimeZoneId,
      });

      dispatch(
        actions.receiveOnlineFollowersHeatmapSucceeded(onlineFollowersHeatmap),
      );
    } catch (e) {
      dispatch(
        actions.receiveOnlineFollowersHeatmapFailed(
          getRequestErrorMessage(e) ??
            'An unknown error eccurred when attempting to fetch the TikTok online followers heatmap',
        ),
      );
    }
  },
);

/**
 * Fetches the best times to post for 7 days
 */
export const fetchBestTimesToPost = createThunk(
  () => async (dispatch, getState) => {
    const state = getState();

    const isFetchingBestTimesToPost = getIsFetchingBestTimesToPost(state);
    const tiktokUserId = getTikTokUserId(state);
    const userTimeZoneId = getUserTimeZone(state);

    if (!isDefined(tiktokUserId) || isFetchingBestTimesToPost) {
      return;
    }

    dispatch(actions.requestBestTimesToPost());

    try {
      const bestTimesToPost = await getAudienceBestTimesToPost({
        tiktokUserId,
        timeZone: userTimeZoneId,
      });

      dispatch(actions.receiveBestTimesToPostSucceeded(bestTimesToPost));
    } catch (e) {
      dispatch(
        actions.receiveBestTimesToPostFailed(
          getRequestErrorMessage(e) ??
            'An unknown error eccurred when attempting to fetch best times to post',
        ),
      );
    }
  },
);

export default reducer;
